import { useEffect, useState } from 'react';

import { BP, MQ, type Surface } from '../styles/breakpoints';

/**
 * The ONE sanctioned way to branch on surface inside TSX.
 *
 * If a screen needs `if (desktop)` more than once outside its layout
 * wrapper, the layout primitive is wrong — fix `StageLayout` / `Page`,
 * not the screen.
 *
 * SSR-safe: returns 'phone' when there's no window, which is also the
 * correct first paint for the base layer.
 */
export function useSurface(): Surface {
  const [surface, setSurface] = useState<Surface>(() => read());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const md = window.matchMedia(MQ.md);
    const lg = window.matchMedia(MQ.lg);
    const onChange = () => setSurface(read());
    // Safari <14 only has addListener; both spellings are cheap to wire.
    md.addEventListener?.('change', onChange);
    lg.addEventListener?.('change', onChange);
    onChange();
    return () => {
      md.removeEventListener?.('change', onChange);
      lg.removeEventListener?.('change', onChange);
    };
  }, []);

  return surface;
}

function read(): Surface {
  if (typeof window === 'undefined') return 'phone';
  const w = window.innerWidth;
  if (w >= BP.lg) return 'desktop';
  if (w >= BP.md) return 'tablet';
  return 'phone';
}

/**
 * True only for a real pointer (mouse / trackpad) on a desktop-width
 * screen. Everything hover-dependent and every keycap hint is gated on
 * this — a 1024px-wide touch tablet gets the desktop *layout* but keeps
 * every affordance visible.
 */
export function useFinePointer(): boolean {
  const [fine, setFine] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia?.(MQ.fine).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(MQ.fine);
    const onChange = () => setFine(mq.matches);
    mq.addEventListener?.('change', onChange);
    onChange();
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return fine;
}

/**
 * Reactive `min-width` test, for the one place a surface isn't enough:
 * StageLayout drops its LEFT zone below 1280 while keeping the right one,
 * and that boundary isn't a named breakpoint.
 */
export function useMinWidth(px: number): boolean {
  const [match, setMatch] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia?.(`(min-width: ${px}px)`).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(min-width: ${px}px)`);
    const onChange = () => setMatch(mq.matches);
    mq.addEventListener?.('change', onChange);
    onChange();
    return () => mq.removeEventListener?.('change', onChange);
  }, [px]);
  return match;
}

/**
 * Desktop layout AND a real pointer — the gate for keyboard choreography.
 * Both hooks are called unconditionally; `&&` on the *results*, never on
 * the calls (short-circuiting a hook call breaks hook order).
 */
export function useKeyboardSurface(): boolean {
  const surface = useSurface();
  const fine = useFinePointer();
  return surface === 'desktop' && fine;
}
